
import { useState } from 'react';
import { supabase } from '../../../lib/supabase';

interface ReviewFormProps {
  productId: number;
  userId: string;
  userName: string;
  onSubmitted: () => void;
  onCancel: () => void;
}

export default function ReviewForm({
  productId,
  userId,
  userName,
  onSubmitted,
  onCancel,
}: ReviewFormProps) {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (rating === 0) {
      setError('Please select a star rating');
      return;
    }
    if (!title.trim()) {
      setError('Please add a review title');
      return;
    }
    if (!body.trim()) {
      setError('Please write your review');
      return;
    }
    if (body.length > 500) {
      setError('Review must be 500 characters or less');
      return;
    }

    setSubmitting(true);

    try {
      const { error: insertError } = await supabase.from('reviews').insert({
        product_id: productId,
        user_id: userId,
        author_name: userName,
        rating,
        title: title.trim(),
        body: body.trim(),
        verified_purchase: false,
        helpful_count: 0,
      });

      if (insertError) {
        throw insertError;
      }

      setSuccess(true);
      setTimeout(() => {
        onSubmitted();
      }, 1500);
    } catch (err) {
      console.error('Error inserting review:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="mb-10 p-8 bg-[#F1F8E9] rounded-xl border border-[#C8E6C9] text-center">
        <div className="w-14 h-14 mx-auto mb-4 bg-[#2E7D32] rounded-full flex items-center justify-center">
          <i className="ri-check-line text-white text-2xl"></i>
        </div>
        <h4 className="text-lg font-serif text-gray-900 mb-1">
          Thank you for your review!
        </h4>
        <p className="text-sm text-gray-600">
          Your feedback helps other customers make informed decisions.
        </p>
      </div>
    );
  }

  return (
    <div className="mb-10 p-8 bg-white rounded-xl border border-gray-200">
      <div className="flex items-center justify-between mb-6">
        <h4 className="text-lg font-serif text-gray-900">Write Your Review</h4>
        <button
          onClick={onCancel}
          className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-xl"></i>
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Star Rating */}
        <div className="mb-6">
          <label className="block text-xs font-semibold text-gray-500 tracking-wider uppercase mb-3">
            Your Rating <span className="text-red-400">*</span>
          </label>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                onClick={() => setRating(star)}
                className="w-9 h-9 flex items-center justify-center cursor-pointer transition-transform hover:scale-110"
              >
                <i
                  className={`text-2xl ${
                    star <= (hoverRating || rating)
                      ? 'ri-star-fill text-[#2E7D32]'
                      : 'ri-star-line text-gray-300'
                  }`}
                />
              </button>
            ))}
            {rating > 0 && (
              <span className="ml-2 text-sm text-gray-500">
                {['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][rating]}
              </span>
            )}
          </div>
        </div>

        {/* Title */}
        <div className="mb-5">
          <label className="block text-xs font-semibold text-gray-500 tracking-wider uppercase mb-2">
            Review Title <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Summarize your experience"
            maxLength={100}
            className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#2E7D32] transition-colors"
          />
        </div>

        {/* Body */}
        <div className="mb-6">
          <label className="block text-xs font-semibold text-gray-500 tracking-wider uppercase mb-2">
            Your Review <span className="text-red-400">*</span>
          </label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, 500))}
            placeholder="What did you like or dislike? How did the product perform?"
            rows={4}
            maxLength={500}
            className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#2E7D32] transition-colors resize-none"
          />
          <p className="text-xs text-gray-400 mt-1 text-right">
            {body.length}/500
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 px-4 py-3 bg-red-50 border border-red-100 rounded-lg flex items-center gap-2">
            <i className="ri-error-warning-line text-red-500 w-4 h-4 flex items-center justify-center"></i>
            <span className="text-sm text-red-600">{error}</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={submitting}
            className="px-8 py-3 bg-[#2E7D32] text-white text-sm font-semibold rounded-lg hover:bg-[#1B5E20] transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting ? (
              <>
                <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center"></i>
                Submitting...
              </>
            ) : (
              <>
                <i className="ri-send-plane-line w-4 h-4 flex items-center justify-center"></i>
                Submit Review
              </>
            )}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
